import type { P2PEngine } from "@p2p-hls/core-engine";
import { domBars, domStacked, lineChart, sparkline, type Bar } from "./charts.js";
import type { PlaybackMetrics } from "./playback-metrics.js";

const PAL = {
  p2p: "#4ade80",
  http: "#f59e0b",
  hedge: "#fb923c",
  cache: "#64748b",
  rtt: "#38bdf8",
  rtt95: "#0e7490",
  peers: "#a78bfa",
};

/** Component styles, injected once so the dashboard is self-contained. */
const DASHBOARD_CSS = `
#dashboard{background:#0b0e14;color:#d7dce5;font:14px/1.5 system-ui,sans-serif}
.cards{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px}
.card{background:#141a24;border:1px solid #222b3a;border-radius:8px;padding:10px 14px;min-width:110px}
.card .v{font-size:20px;font-weight:600;color:#9fe6b0}
.card .l{font-size:10px;color:#7b869c;text-transform:uppercase;letter-spacing:.5px}
.panels{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.panel{background:#141a24;border:1px solid #222b3a;border-radius:8px;padding:12px;min-width:0}
.panel h3{margin:0 0 10px;font-size:12px;color:#9aa4b6;font-weight:500}
canvas.chart{width:100%;height:120px;display:block}
canvas.spark{width:100%;height:48px;display:block}
.bars{display:flex;flex-direction:column;gap:6px}
.bar-row{display:grid;grid-template-columns:84px 1fr 64px;align-items:center;gap:8px;font-size:12px}
.bar-label{color:#9aa4b6;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bar-track{background:#1c2431;border-radius:4px;height:12px;overflow:hidden}
.bar-fill{height:100%;border-radius:4px;transition:width .3s}
.bar-val{text-align:right;font-variant-numeric:tabular-nums;color:#cbd3e1}
.empty{color:#7b869c;font-size:12px;padding:8px 0}
.stacked{display:flex;height:16px;border-radius:4px;overflow:hidden;background:#1c2431}
.stacked>div{height:100%}
.legend{display:flex;flex-wrap:wrap;gap:12px;margin-top:8px;font-size:11px;color:#9aa4b6}
.legend i{display:inline-block;width:10px;height:10px;border-radius:2px;vertical-align:middle}
@media (max-width:900px){.panels{grid-template-columns:1fr}}`;

function injectStyles(): void {
  if (document.getElementById("p2p-dashboard-style")) return;
  const style = document.createElement("style");
  style.id = "p2p-dashboard-style";
  style.textContent = DASHBOARD_CSS;
  document.head.appendChild(style);
}

/** Create an element with optional class + text (no innerHTML — XSS-safe). */
function mk(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function panel(title: string, body: HTMLElement): HTMLElement {
  const p = mk("div", "panel");
  p.append(mk("h3", undefined, title), body);
  return p;
}

/**
 * Per-tab realtime dashboard. Builds its DOM once, then polls
 * engine.metrics.snapshot() + playback metrics on an interval and redraws.
 */
export class Dashboard {
  private readonly cards = new Map<string, HTMLElement>();
  private offloadCanvas!: HTMLCanvasElement;
  private peersCanvas!: HTMLCanvasElement;
  private srcMix!: HTMLElement;
  private latency!: HTMLElement;
  private rttBars!: HTMLElement;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastSig = "";

  constructor(
    private readonly engine: P2PEngine,
    private readonly playback: PlaybackMetrics,
  ) {}

  mount(container: HTMLElement): void {
    injectStyles();
    container.textContent = "";

    const cardsRow = mk("div", "cards");
    const cardDefs: [string, string][] = [
      ["offload", "P2P offload"],
      ["peers", "active peers"],
      ["hedge", "hedge win"],
      ["miss", "deadline miss"],
      ["startup", "startup"],
      ["rebuffer", "rebuffer"],
    ];
    for (const [key, label] of cardDefs) {
      const card = mk("div", "card");
      const v = mk("div", "v", "—");
      card.append(v, mk("div", "l", label));
      cardsRow.appendChild(card);
      this.cards.set(key, v);
    }
    container.appendChild(cardsRow);

    const grid = mk("div", "panels");
    this.offloadCanvas = document.createElement("canvas");
    this.offloadCanvas.className = "chart";
    this.peersCanvas = document.createElement("canvas");
    this.peersCanvas.className = "spark";
    this.srcMix = mk("div");
    this.latency = mk("div", "bars");
    this.rttBars = mk("div", "bars");

    grid.append(
      panel("Offload ratio over time", this.offloadCanvas),
      panel("Source mix (bytes)", this.srcMix),
      panel("Delivery latency p50/p95 (ms)", this.latency),
      panel("RTT per peer p50/p95 (ms)", this.rttBars),
      panel("Active peers", this.peersCanvas),
    );
    container.appendChild(grid);

    this.timer = setInterval(() => this.update(), 500);
    this.update();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private update(): void {
    const s = this.engine.metrics.snapshot();
    const pb = this.playback.snapshot();
    // Skip the full canvas + DOM rebuild when nothing changed since the last tick
    // (poll is 500ms but data advances ~1/s) — avoids needless layout thrash.
    const sig = `${s.series.length}|${s.bytesP2P}|${s.bytesHttp}|${s.activePeers}|${pb.rebufferCount}|${pb.startupMs}`;
    if (sig === this.lastSig) return;
    this.lastSig = sig;
    const pct = (x: number) => `${(x * 100).toFixed(1)}%`;
    const ms = (x: number | null) => (x == null ? "—" : `${Math.round(x)} ms`);

    this.setCard("offload", pct(s.offloadRatio));
    this.setCard("peers", String(s.activePeers));
    this.setCard("hedge", pct(s.hedgeWinRate));
    this.setCard("miss", pct(s.deadline.missRate));
    this.setCard("startup", ms(pb.startupMs));
    this.setCard("rebuffer", `${pb.rebufferCount} · ${ms(pb.rebufferMs)}`);

    lineChart(
      this.offloadCanvas,
      s.series.map((p) => p.offloadRatio),
      { color: PAL.p2p, fill: "rgba(74,222,128,.15)", yMin: 0, yMax: 1, format: (v) => pct(v) },
    );
    sparkline(
      this.peersCanvas,
      s.series.map((p) => p.activePeers),
      PAL.peers,
    );

    const bytes = (n: number) => `${(n / 1048576).toFixed(2)} MB`;
    domStacked(this.srcMix, [
      { label: "P2P", value: s.sources.p2p.bytes, color: PAL.p2p, text: bytes(s.sources.p2p.bytes) },
      { label: "HTTP", value: s.sources.http.bytes, color: PAL.http, text: bytes(s.sources.http.bytes) },
      { label: "hedge", value: s.sources["http-hedge-win"].bytes, color: PAL.hedge, text: bytes(s.sources["http-hedge-win"].bytes) },
      { label: "cache", value: s.sources.cache.bytes, color: PAL.cache, text: bytes(s.sources.cache.bytes) },
    ]);

    this.drawLatency(s.latency);
    this.drawRtt(s.peers);
  }

  private drawLatency(lat: ReturnType<P2PEngine["metrics"]["snapshot"]>["latency"]): void {
    const bars: Bar[] = [
      { label: "P2P p50", value: lat.p2p.p50, color: PAL.p2p },
      { label: "P2P p95", value: lat.p2p.p95, color: PAL.rtt95 },
      { label: "HTTP p50", value: lat.http.p50, color: PAL.http },
      { label: "HTTP p95", value: lat.http.p95, color: PAL.hedge },
    ];
    domBars(this.latency, bars, " ms");
  }

  private drawRtt(peers: ReturnType<P2PEngine["metrics"]["snapshot"]>["peers"]): void {
    if (peers.length === 0) {
      this.rttBars.textContent = "";
      this.rttBars.appendChild(mk("div", "empty", "no peers connected"));
      return;
    }
    const bars: Bar[] = [];
    for (const p of peers) {
      bars.push({ label: `${p.peerId.slice(0, 6)} p50`, value: p.p50, color: PAL.rtt });
      bars.push({ label: `${p.peerId.slice(0, 6)} p95`, value: p.p95, color: PAL.rtt95 });
    }
    domBars(this.rttBars, bars, " ms");
  }

  private setCard(key: string, text: string): void {
    const el = this.cards.get(key);
    if (el) el.textContent = text;
  }
}
